import { useQuery } from '@tanstack/react-query';
import { isToolUIPart, type UIMessage } from 'ai';
import {
  Check,
  ChevronRight,
  CircleAlert,
  LoaderCircle,
  PencilLine,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useTRPC } from '../trpc.js';
import { Markdown } from './Markdown.js';

type ToolPart = Extract<UIMessage['parts'][number], { toolCallId: string }>;

export interface MessageViewProps {
  message: UIMessage;
  /** The person's answer to a confirm card. */
  onDecide: (approvalId: string, approved: boolean) => void;
  /** While an answer is streaming the cards wait, so a click cannot race it. */
  busy: boolean;
}

/**
 * Tools that run for minutes, each shown as a row of its own saying what it is
 * doing and how long it has been at it, rather than folded into the lookups.
 */
const LONG_TOOLS: Readonly<Record<string, { doing: string; usually: string }>> = {
  suggest_investors: { doing: 'Researching follow-on investors for', usually: '1–3 minutes' },
};

/**
 * One message. A question is the person's words; an answer is its text with
 * the lookups between them folded into one line each run of them ("Looked up
 * 3 things"), a long job as its own row, and every change it proposed as a
 * card with its own state.
 */
export function MessageView(props: MessageViewProps): React.JSX.Element {
  const { message } = props;
  if (message.role === 'user') {
    const words = message.parts.flatMap((part) => (part.type === 'text' ? [part.text] : []));
    return <div className="assistant-question">{words.join('\n')}</div>;
  }

  const groups = groupParts(message.parts);
  return (
    <div className="assistant-answer">
      {groups.map((group, index) => {
        switch (group.kind) {
          case 'text':
            return <Markdown key={index} text={group.text} />;
          case 'lookups':
            return <Lookups key={index} parts={group.parts} />;
          case 'job':
            return <JobRow key={index} part={group.part} />;
          case 'change':
            return (
              <ChangeCard
                key={index}
                part={group.part}
                onDecide={props.onDecide}
                busy={props.busy}
              />
            );
          default: {
            const unreachable: never = group;
            return unreachable;
          }
        }
      })}
    </div>
  );
}

type Group =
  | { kind: 'text'; text: string }
  | { kind: 'lookups'; parts: ToolPart[] }
  | { kind: 'job'; part: ToolPart }
  | { kind: 'change'; part: ToolPart };

/** A change is any tool call that asked for a confirmation; the rest were lookups. */
function isChange(part: ToolPart): boolean {
  return part.approval !== undefined && part.approval.isAutomatic !== true;
}

function groupParts(parts: UIMessage['parts']): Group[] {
  const groups: Group[] = [];
  for (const part of parts) {
    const last = groups.at(-1);
    if (part.type === 'text') {
      if (part.text.trim() === '') continue;
      if (last?.kind === 'text') last.text += `\n\n${part.text}`;
      else groups.push({ kind: 'text', text: part.text });
    } else if (isToolUIPart(part)) {
      if (isChange(part)) groups.push({ kind: 'change', part });
      else if (LONG_TOOLS[toolName(part)] !== undefined) groups.push({ kind: 'job', part });
      else if (last?.kind === 'lookups') last.parts.push(part);
      else groups.push({ kind: 'lookups', parts: [part] });
    }
  }
  return groups;
}

function toolName(part: ToolPart): string {
  return part.type === 'dynamic-tool' ? part.toolName : part.type.replace(/^tool-/, '');
}

/** `who_knows` → "Who knows". */
function humanise(name: string): string {
  const words = name.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function isRunning(part: ToolPart): boolean {
  return part.state === 'input-streaming' || part.state === 'input-available';
}

const toolOutput = z.object({
  summary: z.string(),
  error: z.string().optional(),
  detail: z.string().optional(),
});

interface Outcome {
  text: string;
  failed: boolean;
  /** The underlying error, behind a details toggle; never the headline. */
  detail?: string;
}

function outcomeOf(part: ToolPart): Outcome {
  if (part.state === 'output-error') {
    return {
      text: `${humanise(toolName(part))} did not go through.`,
      failed: true,
      detail: part.errorText,
    };
  }
  if (part.state !== 'output-available') return { text: '', failed: false };
  const parsed = toolOutput.safeParse(part.output);
  if (!parsed.success) return { text: '', failed: false };
  if (parsed.data.error !== undefined) {
    return {
      text: parsed.data.error,
      failed: true,
      ...(parsed.data.detail === undefined ? {} : { detail: parsed.data.detail }),
    };
  }
  return { text: parsed.data.summary, failed: false };
}

function Lookups(props: { parts: ToolPart[] }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const running = props.parts.some(isRunning);
  const only = props.parts.length === 1 ? props.parts[0] : undefined;
  const label = running
    ? 'Looking up…'
    : only !== undefined
      ? `Looked up: ${outcomeOf(only).text || humanise(toolName(only))}`
      : `Looked up ${props.parts.length} things`;

  return (
    <div className="assistant-lookups">
      <button
        type="button"
        className="assistant-lookups-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {running ? (
          <LoaderCircle aria-hidden="true" className="assistant-spin" />
        ) : (
          <ChevronRight aria-hidden="true" className={open ? 'assistant-open' : undefined} />
        )}
        <span>{label}</span>
      </button>
      {open && (
        <ul className="assistant-lookups-list">
          {props.parts.map((part) => {
            const outcome = outcomeOf(part);
            return (
              <li key={part.toolCallId} className={outcome.failed ? 'failed' : undefined}>
                <strong>{humanise(toolName(part))}</strong>
                {outcome.text !== '' && <span> — {outcome.text}</span>}
                {outcome.detail !== undefined && <Details text={outcome.detail} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const suggestInput = z.object({
  portfolioCompanyId: z.string().optional(),
  portfolioCompany: z.string().optional(),
});

/** The company a suggestion run is for, by name. */
function useSubject(part: ToolPart): string {
  const trpc = useTRPC();
  const input = suggestInput.safeParse(part.input);
  const id = input.success ? input.data.portfolioCompanyId : undefined;
  const company = useQuery({
    ...trpc.portfolio.get.queryOptions({ id: id ?? '' }),
    enabled: id !== undefined,
    staleTime: Infinity,
  });
  return (
    company.data?.name ?? (input.success ? input.data.portfolioCompany : undefined) ?? 'the company'
  );
}

/** A job that takes minutes: what it is doing and for how long, then its answer. */
function JobRow(props: { part: ToolPart }): React.JSX.Element {
  const { part } = props;
  const long = LONG_TOOLS[toolName(part)];
  const subject = useSubject(part);
  const running = isRunning(part);
  const elapsed = useElapsed(running);
  const outcome = outcomeOf(part);

  if (running) {
    return (
      <div className="assistant-job running">
        <LoaderCircle aria-hidden="true" className="assistant-spin" />
        <span>
          {long?.doing ?? humanise(toolName(part))} {subject}… usually takes{' '}
          {long?.usually ?? 'a few minutes'}
          <span className="assistant-job-elapsed"> · {elapsed}</span>
        </span>
      </div>
    );
  }
  if (outcome.failed) {
    return (
      <div className="assistant-job failed">
        <CircleAlert aria-hidden="true" />
        <span>
          {outcome.text}
          {outcome.detail !== undefined && <Details text={outcome.detail} />}
        </span>
      </div>
    );
  }
  return (
    <div className="assistant-job">
      <Sparkles aria-hidden="true" />
      <span>{outcome.text}</span>
    </div>
  );
}

/** "0:45" since the row first showed, ticking while it runs. */
function useElapsed(running: boolean): string {
  const [started] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [running]);
  const seconds = Math.max(0, Math.floor((now - started) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The real error, for whoever wants it; the sentence above it is for everyone. */
function Details(props: { text: string }): React.JSX.Element {
  return (
    <details className="assistant-details">
      <summary>Details</summary>
      <code>{props.text}</code>
    </details>
  );
}

function ChangeCard(props: {
  part: ToolPart;
  onDecide: (approvalId: string, approved: boolean) => void;
  busy: boolean;
}): React.JSX.Element | null {
  const { part } = props;
  const approval = part.approval;
  if (approval === undefined) return null;
  const [title, ...lines] = (approval.requestReason ?? humanise(toolName(part))).split('\n');

  const result = ((): {
    tone: string;
    icon: React.JSX.Element;
    words: string;
    detail?: string;
  } | null => {
    switch (part.state) {
      case 'approval-requested':
        return null;
      case 'approval-responded':
        return approval.approved === true
          ? { tone: 'pending', icon: <LoaderCircle className="assistant-spin" />, words: 'Saving…' }
          : { tone: 'cancelled', icon: <X />, words: 'Cancelled' };
      case 'output-available':
      case 'output-error': {
        const outcome = outcomeOf(part);
        return outcome.failed
          ? {
              tone: 'failed',
              icon: <CircleAlert />,
              words: `Failed: ${outcome.text}`,
              ...(outcome.detail === undefined ? {} : { detail: outcome.detail }),
            }
          : { tone: 'done', icon: <Check />, words: 'Done' };
      }
      case 'output-denied':
        return { tone: 'cancelled', icon: <X />, words: 'Cancelled' };
      default:
        return {
          tone: 'pending',
          icon: <LoaderCircle className="assistant-spin" />,
          words: 'Saving…',
        };
    }
  })();

  return (
    <div className={`assistant-change${result === null ? ' waiting' : ` ${result.tone}`}`}>
      <div className="assistant-change-head">
        <span className="assistant-change-icon" aria-hidden="true">
          <PencilLine />
        </span>
        <strong>{title}</strong>
      </div>
      {lines.length > 0 && (
        <ul className="assistant-change-lines">
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
      {result === null ? (
        <div className="assistant-change-actions">
          <button
            type="button"
            className="button primary small"
            disabled={props.busy}
            onClick={() => props.onDecide(approval.id, true)}
          >
            <Check aria-hidden="true" />
            Confirm
          </button>
          <button
            type="button"
            className="button small"
            disabled={props.busy}
            onClick={() => props.onDecide(approval.id, false)}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="assistant-change-outcome">
          <span aria-hidden="true">{result.icon}</span>
          <span>
            {result.words}
            {result.detail !== undefined && <Details text={result.detail} />}
          </span>
        </div>
      )}
    </div>
  );
}
